import { useState } from "react";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ENQUIRY_TYPES, BRAND } from "@/lib/brand";

export default function ContactForm({ compact = false, defaultEnquiryType = "general", sourcePage = "contact", testIdPrefix = "contact-form" }) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      city_state: "",
      enquiry_type: defaultEnquiryType,
      message: "",
    },
  });
  const [submitting, setSubmitting] = useState(false);
  const enquiry_type = watch("enquiry_type");

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...values, source_page: sourcePage });
      toast.success("Thank you for contacting Pizza Excellente.", {
        description: "The team will review your message and respond as appropriate.",
        duration: 6000,
      });
      reset({ ...values, full_name: "", email: "", phone: "", city_state: "", message: "" });
    } catch (e) {
      toast.error("We couldn't send your enquiry.", {
        description: formatApiError(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid={`${testIdPrefix}`}
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5"
      noValidate
    >
      <div className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-5`}>
        <div className="space-y-1.5">
          <Label htmlFor={`${testIdPrefix}-full-name`} className="text-xs tracking-[0.15em] uppercase text-pe-ink/70 font-medium">
            Full name
          </Label>
          <Input
            id={`${testIdPrefix}-full-name`}
            data-testid={`${testIdPrefix}-full-name`}
            placeholder="Your name"
            className="rounded-none border-pe-ink/15 bg-white focus-visible:ring-pe-maroon focus-visible:border-pe-maroon h-12"
            {...register("full_name", { required: "Please enter your name", minLength: { value: 2, message: "Name is too short" } })}
          />
          {errors.full_name && <p className="text-xs text-pe-maroon">{errors.full_name.message}</p>}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${testIdPrefix}-email`} className="text-xs tracking-[0.15em] uppercase text-pe-ink/70 font-medium">
            Email address
          </Label>
          <Input
            id={`${testIdPrefix}-email`}
            data-testid={`${testIdPrefix}-email`}
            type="email"
            placeholder="you@email.com"
            className="rounded-none border-pe-ink/15 bg-white focus-visible:ring-pe-maroon focus-visible:border-pe-maroon h-12"
            {...register("email", {
              required: "Please enter your email",
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/i, message: "Please enter a valid email" },
            })}
          />
          {errors.email && <p className="text-xs text-pe-maroon">{errors.email.message}</p>}
        </div>
      </div>

      <div className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-5`}>
        <div className="space-y-1.5">
          <Label htmlFor={`${testIdPrefix}-phone`} className="text-xs tracking-[0.15em] uppercase text-pe-ink/70 font-medium">
            Phone number
          </Label>
          <Input
            id={`${testIdPrefix}-phone`}
            data-testid={`${testIdPrefix}-phone`}
            placeholder="+91 ··· ··· ····"
            className="rounded-none border-pe-ink/15 bg-white focus-visible:ring-pe-maroon focus-visible:border-pe-maroon h-12"
            {...register("phone")}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${testIdPrefix}-city-state`} className="text-xs tracking-[0.15em] uppercase text-pe-ink/70 font-medium">
            City / State
          </Label>
          <Input
            id={`${testIdPrefix}-city-state`}
            data-testid={`${testIdPrefix}-city-state`}
            placeholder="e.g. Greater Noida, UP"
            className="rounded-none border-pe-ink/15 bg-white focus-visible:ring-pe-maroon focus-visible:border-pe-maroon h-12"
            {...register("city_state")}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs tracking-[0.15em] uppercase text-pe-ink/70 font-medium">Enquiry type</Label>
        <Select
          value={enquiry_type}
          onValueChange={(v) => setValue("enquiry_type", v, { shouldValidate: true })}
        >
          <SelectTrigger data-testid={`${testIdPrefix}-enquiry-type`} className="rounded-none border-pe-ink/15 bg-white h-12 focus:ring-pe-maroon focus:border-pe-maroon">
            <SelectValue placeholder="Select enquiry type" />
          </SelectTrigger>
          <SelectContent>
            {ENQUIRY_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value} data-testid={`enquiry-option-${t.value}`}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-message`} className="text-xs tracking-[0.15em] uppercase text-pe-ink/70 font-medium">
          Message
        </Label>
        <Textarea
          id={`${testIdPrefix}-message`}
          data-testid={`${testIdPrefix}-message`}
          rows={compact ? 4 : 6}
          placeholder="Share your background, city of interest, and the level of detail you would like to discuss."
          className="rounded-none border-pe-ink/15 bg-white focus-visible:ring-pe-maroon focus-visible:border-pe-maroon resize-none"
          {...register("message", { required: "Please share a short message", minLength: { value: 10, message: "Please add a few more details" } })}
        />
        {errors.message && <p className="text-xs text-pe-maroon">{errors.message.message}</p>}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
        <p className="text-xs text-pe-ink/60">
          Your message will be sent to{" "}
          <a href={BRAND.mailto} className="underline decoration-pe-maroon/30 hover:text-pe-maroon">
            {BRAND.email}
          </a>
        </p>
        <button
          type="submit"
          data-testid={`${testIdPrefix}-submit`}
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 bg-pe-maroon text-white px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase hover:bg-pe-maroon-deep disabled:opacity-60 transition-colors"
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitting ? "Sending..." : "Send Enquiry"}
        </button>
      </div>
    </form>
  );
}
